import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createSlots as _createSlots } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "align-middle py-2 px-1 table-info" }
const _hoisted_4 = { class: "align-middle py-2 px-1 table-info" }
const _hoisted_5 = { class: "align-middle text-center py-2 px-1 table-info" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { key: 1 }
const _hoisted_11 = ["innerHTML"]
const _hoisted_12 = { key: 1 }

import { computed, reactive, ref, PropType, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatDateTime, deepParseDates } from '@/utils/date-utils';
import { ComputeTaskUtils } from '@/utils/compute-utils';
import { highlightJson } from '@/utils/prism-utils';
import ComputeTaskDebugDetails from '@/components/compute/ComputeTaskDebugDetails.vue';
import { useQuery } from '@vue/apollo-composable';
import { useVisibleQueryPolling } from '@/composables/visible-query-polling';
import {
  BucketType,
  ComputeTasksDocument,
  ComputeTasksLiteDocument,
  ComputeTaskType,
  ProjectType,
} from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BModal,
  BPagination,
  BProgress,
  BProgressBar,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import {
  IS_DEBUG_MODE,
  COMPUTE_TASK_STATUS,
  COMPUTE_TASK_TYPES,
  PAGING_ITEMS_PER_PAGE,
} from '@/constants';
import { useUserStore } from '@/store/user';
import ComputeTaskAbort from '@/components/compute/ComputeTaskAbort.vue';
import { formatFileSize } from '@/utils/file-utils';
import { openRouterLink } from '@/utils/router-utils';


export default /*@__PURE__*/_defineComponent({
  __name: 'ComputeTaskList',
  props: {
  // component properties
  parentName: { type: String as PropType<string> },
  // optional placeholder
  placeholder: { type: String as PropType<string | null>, default: null },
  // search filter
  acrossUsers: { type: Boolean, default: false },
  taskId: { type: Number as PropType<number | null>, default: null },
  parentId: { type: Number as PropType<number | null>, default: null },
  userId: { type: Number as PropType<number | null>, default: null },
  userRole: { type: String as PropType<string | null>, default: null },
  projectId: { type: Number as PropType<number | null>, default: null },
  bucketId: { type: Number as PropType<number | null>, default: null },
  taskType: { type: String as PropType<string | null>, default: null },
  taskTypes: { type: Array as PropType<string[] | null>, default: null },
  status: { type: String as PropType<string | null>, default: null },
  namePattern: { type: String as PropType<string | null>, default: null },
  descriptionPattern: {
    type: String as PropType<string | null>,
    default: null,
  },
  // date & time filters
  createdAfter: { type: String as PropType<string | null>, default: null },
  createdBefore: { type: String as PropType<string | null>, default: null },
  updatedAfter: { type: String as PropType<string | null>, default: null },
  updatedBefore: { type: String as PropType<string | null>, default: null },
  completedBefore: { type: String as PropType<string | null>, default: null },
  completedAfter: { type: String as PropType<string | null>, default: null },
  abortedAfter: { type: String as PropType<string | null>, default: null },
  abortedBefore: { type: String as PropType<string | null>, default: null },
  // paging specification
  pagingItemsPerPage: { type: Number, default: PAGING_ITEMS_PER_PAGE },
  pagingHideControls: { type: Boolean, default: false },
  // additinal component props
  transparentStyle: { type: Boolean, default: false },
  messageNoItemsFound: {
    type: String,
    default: 'No compute tasks found.',
  },
  taskNameColumnTitle: {
    type: String,
    default: 'Task Name',
  },
  preventRedirectToBucket: {
    type: Boolean,
    default: false,
  },
  showBucketButton: {
    type: Boolean,
    default: true,
  },
  showExecDetails: {
    type: Boolean,
    default: true,
  },
  showAbortButton: {
    type: Boolean,
    default: true,
  },
  queryDocument: {
    type: Object as PropType<
      typeof ComputeTasksDocument | typeof ComputeTasksLiteDocument
    >,
    default: ComputeTasksDocument,
  },
},
  emits: ["bucket-request"],
  setup(__props, { expose: __expose, emit: __emit }) {

const userStore = useUserStore();
const router = useRouter();

// --- Component Properties ---
const props = __props;

// --- Component State ---
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return tasksQueryResult.value?.computeTasks?.total || 0;
});
const showPlaceholderRow = computed(
  () => pagingPageCurrent.value === 1 && props.placeholder
);

// --- Emits ---
const emits = __emit;

// --- Compute Tasks List ---
const tasks = computed<ComputeTaskType[]>(() => {
  return (
    tasksQueryResult.value?.computeTasks?.computeTasks
      ?.filter((task) => task && task !== null)
      .map((task) => ({
        ...(deepParseDates(task) as ComputeTaskType),
      })) || []
  );
});

const noTasks = computed(() => {
  return tasks.value.length === 0;
});

const selectedTask = ref<ComputeTaskType | null>(null);
const showConfirmAbortModal = ref<boolean>(false);

const taskFields = [
  {
    key: 'createdAt',
    label: 'Created At',
    tdClass: 'align-middle',
    sortable: false,
    formatter: formatDateTime,
  },
  {
    key: 'name',
    label: props.taskNameColumnTitle,
    sortable: false,
    tdClass: 'align-middle',
  },
  { key: 'status', label: 'Status', sortable: false, tdClass: 'align-middle' },
  { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
];

const tooltipFieldsFtpTransfer = [
  {
    key: 'filename',
    label: 'File',
    sortable: false,
    tdClass: 'align-middle text-start',
    thClass: 'text-start',
  },
  {
    key: 'progress',
    label: 'Progress',
    sortable: false,
    tdClass: 'align-middle text-end',
    thClass: 'text-end',
  },
];

const tooltipFieldsBasespaceTransfer = [
  {
    key: 'fileName',
    label: 'File',
    sortable: false,
    tdClass: 'align-middle text-start',
    thClass: 'text-start',
  },
  {
    key: 'size',
    label: 'Size',
    sortable: false,
    tdClass: 'align-middle text-end',
    thClass: 'text-end',
  },
  {
    key: 'progress',
    label: 'Progress',
    sortable: false,
    tdClass: 'align-middle text-end',
    thClass: 'text-end',
  },
];

// --- Query Compute Tasks ---
const {
  result: tasksQueryResult,
  loading: tasksQueryInProgress,
  error: tasksQueryError,
  refetch: refetchTasks,
  onResult: onTasksQueryDone,
  onError: onTasksQueryError,
  query: taskPollingQuery,
} = useQuery(props.queryDocument, {
  acrossUsers: props.acrossUsers,
  id: props.taskId,
  parentId: props.parentId,
  userId: props.userId,
  userRole: props.userRole,
  projectId: props.projectId,
  bucketId: props.bucketId,
  taskType: props.taskType,
  taskTypes: props.taskTypes,
  status: props.status,
  namePattern: props.namePattern,
  descriptionPattern: props.descriptionPattern,
  // date & time filters
  createdAfter: props.createdAfter,
  createdBefore: props.createdBefore,
  updatedAfter: props.updatedAfter,
  updatedBefore: props.updatedBefore,
  completedBefore: props.completedBefore,
  completedAfter: props.completedAfter,
  abortedAfter: props.abortedAfter,
  abortedBefore: props.abortedBefore,
  // paging specification
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: props.pagingItemsPerPage,
});
useVisibleQueryPolling(taskPollingQuery);

onTasksQueryDone((res) => {
  console.debug('Tasks response retrieved');
  if (res.data) {
    if (res.data.computeTasks) {
      console.debug('Tasks retrieved successfuly');
    } else {
      console.error('Error, could not retrieve tasks:', res.data);
    }
  } else if (!res.loading) {
    console.error('Error, could not retrieve tasks result data:', res);
  }
});

onTasksQueryError((err) => {
  console.error('Error fetching tasks (onTasksQueryError):', err.message);
});

function requestRefetchTasks() {
  // ensure integer/null values where needed
  const intTaskId = props.taskId ? parseInt(props.taskId.toString()) : null;
  const intParentId = props.parentId
    ? parseInt(props.parentId.toString())
    : null;
  const intUserId = props.userId ? parseInt(props.userId.toString()) : null;
  const intProjectId = props.projectId
    ? parseInt(props.projectId.toString())
    : null;
  const intBucketId = props.bucketId
    ? parseInt(props.bucketId.toString())
    : null;

  // refetch the tasks with the current filters
  refetchTasks({
    acrossUsers: props.acrossUsers,
    id: intTaskId,
    parentId: intParentId,
    userId: intUserId,
    userRole: props.userRole,
    projectId: intProjectId,
    bucketId: intBucketId,
    taskType: props.taskType,
    taskTypes: props.taskTypes,
    status: props.status,
    namePattern: props.namePattern,
    descriptionPattern: props.descriptionPattern,
    // date & time filters
    createdAfter: props.createdAfter,
    createdBefore: props.createdBefore,
    updatedAfter: props.updatedAfter,
    updatedBefore: props.updatedBefore,
    completedBefore: props.completedBefore,
    completedAfter: props.completedAfter,
    abortedAfter: props.abortedAfter,
    abortedBefore: props.abortedBefore,
    // paging specification
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: props.pagingItemsPerPage,
  })
    ?.catch((err) => {
      console.error('Refetch tasks failed:', err);
    })
    .then(() => {
      console.debug('Refetched tasks');
    });
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchTasks();
});

function requestSearch() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1; // this will trigger the watcher -> requestRefetchTasks
  } else {
    requestRefetchTasks();
  }
}

// --- Compute Task Abort Request ---
function taskAbortRequested(task: ComputeTaskType) {
  if (!task.id) {
    console.error('Task cancellation requested, but task ID is missing');
    return;
  }
  console.debug('Cancel task ', task.id, 'requested, waiting for confirmation');
  selectedTask.value = task;
  showConfirmAbortModal.value = true;
}

function closeTaskAbortModal() {
  showConfirmAbortModal.value = false;
  selectedTask.value = null;
}

// --- Tooltip functions ---
const tooltipVisibility = reactive<{ [taskId: number]: boolean }>({});

function showTooltip(taskId: number) {
  // Hide all other tooltips.
  for (const key in tooltipVisibility) {
    const id = Number(key);
    if (id !== taskId) {
      tooltipVisibility[id] = false;
    }
  }
  tooltipVisibility[taskId] = true;
}

function hideAllTooltips() {
  for (const key in tooltipVisibility) {
    const id = Number(key);
    tooltipVisibility[id] = false;
  }
}

function bucketRequested(bucket: BucketType, index?: number, evt?: MouseEvent) {
  if (bucket && bucket.id) {
    // emit the event to the parent component
    emits('bucket-request', bucket.id);

    // redirect to bucket detail page if permitted
    if (!bucket.deletedAt && !props.preventRedirectToBucket) {
      openRouterLink(
        {
          name: 'BucketDetail',
          params: { id: bucket.id },
          query: {
            returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
            returnLabel: props.parentName,
          },
        },
        router,
        evt
      );
    }
  }
}

function projectRequested(
  project: ProjectType,
  index?: number,
  evt?: MouseEvent
) {
  if (project && project.id) {
    openRouterLink(
      {
        name: 'ProjectDetail',
        params: { id: project.id },
        query: {
          returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
          returnLabel: props.parentName,
        },
      },
      router,
      evt
    );
  }
}

// --- Expose Properties to the Outside ---
__expose({
  tasks,
  tasksQueryInProgress,
  tasksQueryError,
  requestSearch,
});

// --- Debug Details ---
const debugTaskId = ref<number | null>(null);
const detailsContent = ref<any>(null);
const detailsModalTitle = ref('Compute Task Details');
const showDetailsModal = ref(false);

function showExecDetails(task: ComputeTaskType) {
  detailsModalTitle.value = task.name || 'Compute Task Exec Details';
  let censoredAttributes = task.attributes;

  // only allow certain attributes to be copied and viewed if not admin/developer
  if (task.attributes && (!userStore.admin || !userStore.developer)) {
    censoredAttributes = Object.fromEntries(
      Object.entries(task.attributes).filter(([key]) =>
        [
          // common attribues
          'samples', // bcl to fastq conversion + data pipeline
          'version',
          // ftp transfer
          'ftpCmd',
          'autoAnnotate',
          'reconstructData',
          'createProject',
          // data pipeline
          'startPhase',
          'endPhase',
          'bucketOutput',
        ].includes(key)
      )
    );
    // if task.attributes.samples is available, convert the array of objects to array of strings by taking the object's name property only
    if (Array.isArray(censoredAttributes.samples)) {
      censoredAttributes.samples = censoredAttributes.samples.map(
        (sample: { name: string }) => sample.name
      );
    }
  }
  detailsContent.value = censoredAttributes;
  showDetailsModal.value = true;
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (_unref(tasksQueryInProgress))
      ? (_openBlock(), _createBlock(_unref(BAlert), {
          key: 0,
          variant: "info"
        }, {
          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
            _createTextVNode("Loading...", -1)
          ]))]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (_unref(tasksQueryError))
      ? (_openBlock(), _createBlock(_unref(BAlert), {
          key: 1,
          variant: "danger"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(tasksQueryError).message) + " ", 1),
            _createVNode(_unref(BButton), {
              onClick: requestRefetchTasks,
              variant: "outline-dark",
              class: "btn-sm"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_font_awesome_icon, { icon: "sync" }),
                _cache[10] || (_cache[10] = _createTextVNode(" Retry ", -1))
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      modelValue: noTasks.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((noTasks).value = $event)),
      variant: "secondary"
    }, {
      default: _withCtx(() => [
        (_unref(tasksQueryInProgress))
          ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
              _cache[11] || (_cache[11] = _createTextVNode(" Loading tasks ", -1)),
              _createVNode(_unref(BSpinner), { small: "" })
            ]))
          : (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(props.messageNoItemsFound), 1))
      ]),
      _: 1
    }, 8, ["modelValue"]),
    (
        !props.pagingHideControls &&
        __props.pagingItemsPerPage > 0 &&
        pagingTotalRows.value > props.pagingItemsPerPage
      )
      ? (_openBlock(), _createBlock(_unref(BPagination), {
          key: 2,
          modelValue: pagingPageCurrent.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((pagingPageCurrent).value = $event)),
          "total-rows": pagingTotalRows.value,
          "per-page": __props.pagingItemsPerPage,
          pills: "",
          size: "sm",
          class: "mb-2"
        }, null, 8, ["modelValue", "total-rows", "per-page"]))
      : _createCommentVNode("", true),
    (!_unref(tasksQueryError) && tasks.value.length > 0)
      ? (_openBlock(), _createBlock(_unref(BCard), {
          key: 3,
          class: _normalizeClass({ 'transparent-card': props.transparentStyle })
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BTable), {
              items: tasks.value,
              fields: taskFields,
              busy: _unref(tasksQueryInProgress),
              "loading-text": 'Loading tasks...',
              onMouseleave: hideAllTooltips,
              striped: !props.transparentStyle,
              "thead-tr-class": "transparent-table",
              "tbody-tr-class": "transparent-table",
              class: _normalizeClass([
          'browser-table',
          'm-0',
          {
            'table-transparent': props.transparentStyle,
          },
        ]),
              small: "",
              responsive: "",
              hover: ""
            }, _createSlots({
              "cell(status)": _withCtx((data) => [
                _createVNode(_unref(BProgress), {
                  max: "100",
                  onMouseenter: ($event: any) => (showTooltip(data.item.id)),
                  id: `task-progress-${data.item.id}`,
                  animated: _unref(ComputeTaskUtils).progressActive(data.item.status)
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(BProgressBar), {
                      value: "100",
                      variant: _unref(ComputeTaskUtils).progressVariant(data.item.status)
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("span", null, _toDisplayString(_unref(ComputeTaskUtils).progressText(data.item)), 1)
                      ]),
                      _: 2
                    }, 1032, ["variant"])
                  ]),
                  _: 2
                }, 1032, ["onMouseenter", "id", "animated"]),
                (
              _unref(ComputeTaskUtils).progressTooltipHasContent(data.item) &&
              tooltipVisibility[data.item.id]
            )
                  ? (_openBlock(), _createBlock(_unref(BTooltip), {
                      key: 0,
                      class: "large-tooltip",
                      placement: "left",
                      modelValue: tooltipVisibility[data.item.id],
                      "onUpdate:modelValue": ($event: any) => ((tooltipVisibility[data.item.id]) = $event),
                      target: `task-progress-${data.item.id}`,
                      delay: { show: 0, hide: 30000 }
                    }, {
                      default: _withCtx(() => [
                        (
                data.item.status === _unref(COMPUTE_TASK_STATUS).FAILED &&
                _unref(ComputeTaskUtils).errorMessage(data.item)
              )
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createTextVNode(_toDisplayString(_unref(ComputeTaskUtils).errorMessage(data.item)), 1)
                            ], 64))
                          : (
                data.item.taskType === _unref(COMPUTE_TASK_TYPES).BASESPACE_TRANSFER
              )
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                (
                  _unref(ComputeTaskUtils).fileProgressDetails_BasespaceTransfer(
                    data.item
                  ).length > 0
                )
                                  ? (_openBlock(), _createBlock(_unref(BTable), {
                                      key: 0,
                                      items: 
                  _unref(ComputeTaskUtils).fileProgressDetails_BasespaceTransfer(
                    data.item
                  )
                ,
                                      fields: tooltipFieldsBasespaceTransfer,
                                      striped: "",
                                      small: "",
                                      responsive: "",
                                      variant: "dark",
                                      class: "browser-table m-1"
                                    }, {
                                      "cell(fileName)": _withCtx((file) => [
                                        _createElementVNode("small", null, _toDisplayString(file.value), 1)
                                      ]),
                                      "cell(size)": _withCtx((file) => [
                                        _createElementVNode("small", null, _toDisplayString(_unref(formatFileSize)(file.value)), 1)
                                      ]),
                                      "cell(progress)": _withCtx((file) => [
                                        _createElementVNode("small", null, _toDisplayString(Math.round(file.value)) + "%", 1)
                                      ]),
                                      _: 1
                                    }, 8, ["items"]))
                                  : _createCommentVNode("", true)
                              ], 64))
                            : (data.item.taskType === _unref(COMPUTE_TASK_TYPES).FTP_TRANSFER)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                  (
                  _unref(ComputeTaskUtils).fileProgressDetails_FtpTranfer(data.item)
                    .length > 0
                )
                                    ? (_openBlock(), _createBlock(_unref(BTable), {
                                        key: 0,
                                        items: 
                  _unref(ComputeTaskUtils).fileProgressDetails_FtpTranfer(data.item)
                ,
                                        fields: tooltipFieldsFtpTransfer,
                                        striped: "",
                                        small: "",
                                        responsive: "",
                                        variant: "dark",
                                        class: "browser-table m-1"
                                      }, {
                                        "cell(filename)": _withCtx((file) => [
                                          _createElementVNode("small", null, _toDisplayString(file.value), 1)
                                        ]),
                                        "cell(progress)": _withCtx((file) => [
                                          _createElementVNode("small", null, _toDisplayString(Math.round(file.value)) + "% ", 1)
                                        ]),
                                        _: 1
                                      }, 8, ["items"]))
                                    : _createCommentVNode("", true)
                                ], 64))
                              : (
                data.item.status === _unref(COMPUTE_TASK_STATUS).RUNNING &&
                data.item.results.progressDescription
              )
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                    _createTextVNode(_toDisplayString(data.item.results.progressDescription), 1)
                                  ], 64))
                                : _createCommentVNode("", true)
                      ]),
                      _: 2
                    }, 1032, ["modelValue", "onUpdate:modelValue", "target"]))
                  : _createCommentVNode("", true)
              ]),
              "cell(actions)": _withCtx((data) => [
                _createVNode(_unref(BButtonGroup), null, {
                  default: _withCtx(() => [
                    (props.showBucketButton && !props.bucketId)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 0,
                          size: "sm",
                          variant: "outline-secondary",
                          id: `link-bucket-${data.item.id}`,
                          style: _normalizeStyle(
                !data.item.bucket || !data.item.bucket.id ? 'opacity: 0.5' : ''
              ),
                          onClick: [
                            ($event: any) => (bucketRequested(data.item.bucket, data.item.id, $event)),
                            _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                          ]
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: "database" })
                          ]),
                          _: 1
                        }, 8, ["id", "style", "onClick"]))
                      : _createCommentVNode("", true),
                    (!props.projectId && !props.bucketId)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 1,
                          size: "sm",
                          variant: "outline-secondary",
                          id: `link-project-${data.item.id}`,
                          style: _normalizeStyle(
                !data.item.project || !data.item.project.id
                  ? 'opacity: 0.5'
                  : ''
              ),
                          onClick: [
                            ($event: any) => (projectRequested(data.item.project, data.item.id, $event)),
                            _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                          ]
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: "diagram-project" })
                          ]),
                          _: 1
                        }, 8, ["id", "style", "onClick"]))
                      : _createCommentVNode("", true),
                    (props.showExecDetails)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 2,
                          id: `task-exec-${data.item.id}`,
                          variant: "outline-secondary",
                          size: "sm",
                          onClick: ($event: any) => (showExecDetails(data.item))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'sliders'] })
                          ]),
                          _: 1
                        }, 8, ["id", "onClick"]))
                      : _createCommentVNode("", true),
                    (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 3,
                          id: `task-debug-${data.item.id}`,
                          variant: "outline-secondary",
                          size: "sm",
                          onClick: ($event: any) => (debugTaskId.value = data.item.id || null)
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'code'] })
                          ]),
                          _: 1
                        }, 8, ["id", "onClick"]))
                      : _createCommentVNode("", true),
                    (props.showAbortButton)
                      ? (_openBlock(), _createBlock(_unref(BButton), {
                          key: 4,
                          size: "sm",
                          variant: "outline-danger",
                          id: `link-abort-${data.item.id}`,
                          disabled: !_unref(ComputeTaskUtils).canBeAborted(data.item.status),
                          onClick: ($event: any) => (taskAbortRequested(data.item))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_font_awesome_icon, { icon: "times" })
                          ]),
                          _: 1
                        }, 8, ["id", "disabled", "onClick"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1024),
                (props.showBucketButton && !props.bucketId)
                  ? (_openBlock(), _createBlock(_unref(BTooltip), {
                      key: 0,
                      target: `link-bucket-${data.item.id}`
                    }, {
                      default: _withCtx(() => [
                        (data.item.bucket.deletedAt)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                              _createVNode(_component_font_awesome_icon, { icon: "exclamation-triangle" }),
                              _cache[13] || (_cache[13] = _createTextVNode(" Data have been deleted ", -1))
                            ]))
                          : (data.item.bucket)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_7, [
                                _createVNode(_component_font_awesome_icon, {
                                  icon: "database",
                                  class: "me-2"
                                }),
                                _createElementVNode("strong", null, _toDisplayString(data.item.bucket.friendlyName), 1)
                              ]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_8, [
                                _createVNode(_component_font_awesome_icon, { icon: "exclamation-triangle" }),
                                _cache[14] || (_cache[14] = _createTextVNode(" No Data ", -1))
                              ]))
                      ]),
                      _: 2
                    }, 1032, ["target"]))
                  : _createCommentVNode("", true),
                (!props.projectId && !props.bucketId)
                  ? (_openBlock(), _createBlock(_unref(BTooltip), {
                      key: 1,
                      target: `link-project-${data.item.id}`
                    }, {
                      default: _withCtx(() => [
                        (data.item.project)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
                              _cache[15] || (_cache[15] = _createTextVNode(" Project: ", -1)),
                              _createElementVNode("strong", null, _toDisplayString(data.item.project.name), 1)
                            ]))
                          : (_openBlock(), _createElementBlock("span", _hoisted_10, "No Project"))
                      ]),
                      _: 2
                    }, 1032, ["target"]))
                  : _createCommentVNode("", true),
                (props.showExecDetails)
                  ? (_openBlock(), _createBlock(_unref(BTooltip), {
                      key: 2,
                      target: `task-exec-${data.item.id}`,
                      class: "large-tooltip",
                      placement: "left"
                    }, {
                      default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                        _createTextVNode(" Show exec details ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["target"]))
                  : _createCommentVNode("", true),
                (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
                  ? (_openBlock(), _createBlock(_unref(BTooltip), {
                      key: 3,
                      target: `task-debug-${data.item.id}`,
                      class: "large-tooltip",
                      placement: "left"
                    }, {
                      default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                        _createTextVNode(" Show debug details ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["target"]))
                  : _createCommentVNode("", true),
                (props.showAbortButton)
                  ? (_openBlock(), _createBlock(_unref(BTooltip), {
                      key: 4,
                      target: `link-abort-${data.item.id}`
                    }, {
                      default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                        _createElementVNode("span", null, "Abort", -1)
                      ]))]),
                      _: 1
                    }, 8, ["target"]))
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, [
              (showPlaceholderRow.value)
                ? {
                    name: "top-row",
                    fn: _withCtx(() => [
                      _createElementVNode("td", _hoisted_3, _toDisplayString(_unref(formatDateTime)(new Date(Date.now()))), 1),
                      _createElementVNode("td", _hoisted_4, _toDisplayString(props.placeholder), 1),
                      _createElementVNode("td", _hoisted_5, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: "spinner",
                          spin: "",
                          class: "me-2"
                        })
                      ]),
                      _cache[12] || (_cache[12] = _createElementVNode("td", { class: "align-middle text-center py-2 px-1 table-info" }, null, -1))
                    ]),
                    key: "0"
                  }
                : undefined
            ]), 1032, ["items", "busy", "striped", "class"])
          ]),
          _: 1
        }, 8, ["class"]))
      : _createCommentVNode("", true),
    (
        !props.pagingHideControls &&
        props.pagingItemsPerPage > 0 &&
        pagingTotalRows.value > props.pagingItemsPerPage
      )
      ? (_openBlock(), _createBlock(_unref(BPagination), {
          key: 4,
          modelValue: pagingPageCurrent.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((pagingPageCurrent).value = $event)),
          "total-rows": pagingTotalRows.value,
          "per-page": __props.pagingItemsPerPage,
          pills: "",
          size: "sm",
          class: "mt-2 mb-0"
        }, null, 8, ["modelValue", "total-rows", "per-page"]))
      : _createCommentVNode("", true),
    (debugTaskId.value)
      ? (_openBlock(), _createBlock(ComputeTaskDebugDetails, {
          key: debugTaskId.value,
          "task-id": debugTaskId.value,
          "project-id": props.projectId,
          onClose: _cache[5] || (_cache[5] = ($event: any) => (debugTaskId.value = null))
        }, null, 8, ["task-id", "project-id"]))
      : _createCommentVNode("", true),
    _createVNode(_unref(BModal), {
      modelValue: showDetailsModal.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((showDetailsModal).value = $event)),
      class: _normalizeClass([detailsContent.value ? 'modal-xl' : 'modal-sm']),
      onClose: _cache[8] || (_cache[8] = ($event: any) => {
        showDetailsModal.value = false;
        detailsContent.value = null;
      })
    }, {
      header: _withCtx(() => [
        _createElementVNode("h2", null, [
          _createVNode(_component_font_awesome_icon, {
            icon: "sliders",
            class: "me-2"
          }),
          _createTextVNode(" " + _toDisplayString(detailsModalTitle.value), 1)
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          variant: "secondary",
          onClick: _cache[6] || (_cache[6] = ($event: any) => {
            showDetailsModal.value = false;
            detailsContent.value = null;
          })
        }, {
          default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
            _createTextVNode(" Close ", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          (detailsContent.value)
            ? (_openBlock(), _createElementBlock("pre", {
                key: 0,
                class: "code-highlight",
                innerHTML: _unref(highlightJson)(detailsContent.value)
              }, null, 8, _hoisted_11))
            : (_openBlock(), _createElementBlock("p", _hoisted_12, [
                _createVNode(_component_font_awesome_icon, { icon: "exclamation-triangle" }),
                _cache[19] || (_cache[19] = _createElementVNode("strong", null, "No Details", -1))
              ]))
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "class"]),
    (showConfirmAbortModal.value && selectedTask.value)
      ? (_openBlock(), _createBlock(ComputeTaskAbort, {
          key: 6,
          show: showConfirmAbortModal.value,
          task: selectedTask.value,
          onClose: closeTaskAbortModal,
          onCloseAndRefresh: closeTaskAbortModal
        }, null, 8, ["show", "task"]))
      : _createCommentVNode("", true)
  ]))
}
}

})